import { useState, useEffect } from 'react';
import axios from 'axios';
import ManagerLayout from '../components/ManagerLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Download, Edit, Printer, Trash2, Archive } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagerTimesheets = ({ manager, onLogout }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    location_id: '',
    employee_id: '',
    status: [],  // Changed to array for multi-select
    preset: '',
  });
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editForm, setEditForm] = useState({
    clock_in: '',
    clock_out: '',
    reason_for_change: '',
  });
  const [currentView, setCurrentView] = useState('timesheets'); // timesheets, overtime, stat-holiday
  const [overtimeData, setOvertimeData] = useState([]);
  const [statHolidayData, setStatHolidayData] = useState([]);

  const getPresetDates = (preset) => {
    const now = new Date();
    const getMondayOfWeek = (date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day; // If Sunday, go back 6 days, else go to Monday
      d.setDate(d.getDate() + diff);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    switch (preset) {
      case 'prior_week': {
        // Prior week - Mon to Sun (last Monday to Sunday)
        const lastMonday = getMondayOfWeek(now);
        lastMonday.setDate(lastMonday.getDate() - 7); // Go back one week
        const lastSunday = new Date(lastMonday);
        lastSunday.setDate(lastMonday.getDate() + 6);
        lastSunday.setHours(23, 59, 59, 999);
        return { start: lastMonday, end: lastSunday };
      }
      case 'prior_2_weeks': {
        // Prior 2 weeks - Mon to Sun (14 days ending last Sunday)
        const lastMonday = getMondayOfWeek(now);
        lastMonday.setDate(lastMonday.getDate() - 7); // Go to start of prior week
        const start = new Date(lastMonday);
        start.setDate(lastMonday.getDate() - 7); // Go back another week
        const lastSunday = new Date(lastMonday);
        lastSunday.setDate(lastMonday.getDate() + 6);
        lastSunday.setHours(23, 59, 59, 999);
        return { start, end: lastSunday };
      }
      case 'first_half': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 14);
        return { start, end };
      }
      case 'second_half': {
        const start = new Date(now.getFullYear(), now.getMonth(), 15);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of month
        return { start, end };
      }
      default:
        return null;
    }
  };

  const handlePresetChange = (preset) => {
    if (!preset || preset === 'custom') {
      setFilters({ ...filters, preset: 'custom', start_date: '', end_date: '' });
      return;
    }

    const dates = getPresetDates(preset);
    if (dates) {
      setFilters({
        ...filters,
        preset,
        start_date: dates.start.toISOString().split('T')[0],
        end_date: dates.end.toISOString().split('T')[0],
      });
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchEmployees();
    fetchTimesheets();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('managerToken');
      const params = new URLSearchParams();

      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.location_id) params.append('location_id', filters.location_id);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      
      // Handle multi-select status filter
      if (filters.status && filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }

      const response = await axios.get(`${API}/timesheets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setTimesheets(response.data);
      setSelectedEntries([]); // Clear selections when fetching new data
      setSelectAll(false);
    } catch (error) {
      toast.error('Error fetching timesheets');
    } finally {
      setLoading(false);
    }
  };

  const fetchOvertimeData = async () => {
    // If no date range selected, display empty state (don't prevent viewing)
    if (!filters.start_date || !filters.end_date) {
      setOvertimeData([]);
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('managerToken');
      const params = new URLSearchParams();
      
      params.append('start_date', filters.start_date);
      params.append('end_date', filters.end_date);
      
      const response = await axios.get(`${API}/reports/overtime?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Parse CSV response to display on screen
      const csvText = response.data;
      const rows = csvText.split('\n').filter(row => row.trim());
      const headers = rows[0].split(',');
      const data = rows.slice(1).map(row => {
        const values = row.split(',');
        const obj = {};
        headers.forEach((header, i) => {
          obj[header.replace(/"/g, '').trim()] = values[i]?.replace(/"/g, '').trim() || '';
        });
        return obj;
      });
      
      setOvertimeData(data);
    } catch (error) {
      // Show message but don't prevent viewing
      setOvertimeData([]);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatHolidayData = async () => {
    // If no date range selected, display empty state (don't prevent viewing)
    if (!filters.start_date || !filters.end_date) {
      setStatHolidayData([]);
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('managerToken');
      const params = new URLSearchParams();
      
      params.append('start_date', filters.start_date);
      params.append('end_date', filters.end_date);
      
      const response = await axios.get(`${API}/reports/stat-holiday-pay?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Parse CSV response to display on screen
      const csvText = response.data;
      const rows = csvText.split('\n').filter(row => row.trim());
      
      // Helper function to parse CSV row respecting quoted values
      const parseCSVRow = (row) => {
        const values = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < row.length; i++) {
          const char = row[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.replace(/"/g, '').trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.replace(/"/g, '').trim());
        return values;
      };
      
      const headers = parseCSVRow(rows[0]);
      const data = rows.slice(1).map(row => {
        const values = parseCSVRow(row);
        const obj = {};
        headers.forEach((header, i) => {
          obj[header] = values[i] || '';
        });
        return obj;
      });
      
      setStatHolidayData(data);
    } catch (error) {
      // Show message but don't prevent viewing
      setStatHolidayData([]);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      let exportUrl = '';
      let filename = '';

      if (currentView === 'timesheets') {
        // Export timesheets with current filters
        const params = new URLSearchParams();
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.location_id) params.append('location_id', filters.location_id);
        if (filters.employee_id) params.append('employee_id', filters.employee_id);
        if (filters.status && filters.status.length > 0) {
          params.append('status', filters.status.join(','));
        }
        if (token) params.append('token', token);
        
        exportUrl = `${API}/timesheets/export?${params.toString()}`;
        filename = `timesheets_${new Date().toISOString().split('T')[0]}.csv`;
      } else if (currentView === 'overtime') {
        // Export overtime report
        const params = new URLSearchParams();
        params.append('start_date', filters.start_date);
        params.append('end_date', filters.end_date);
        if (token) params.append('token', token);
        
        exportUrl = `${API}/reports/overtime?${params.toString()}`;
        filename = `overtime_report_${new Date().toISOString().split('T')[0]}.csv`;
      } else if (currentView === 'stat-holiday') {
        // Export stat holiday report
        const params = new URLSearchParams();
        params.append('start_date', filters.start_date);
        params.append('end_date', filters.end_date);
        if (token) params.append('token', token);
        
        exportUrl = `${API}/reports/stat-holiday-pay?${params.toString()}`;
        filename = `stat_holiday_report_${new Date().toISOString().split('T')[0]}.csv`;
      }

      // Create a form and submit it for download
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = exportUrl;
      form.style.display = 'none';
      
      if (token) {
        const tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = 'token';
        tokenInput.value = token;
        form.appendChild(tokenInput);
      }
      
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      
      toast.success(`${currentView === 'timesheets' ? 'Timesheets' : currentView === 'overtime' ? 'Overtime Report' : 'Stat Holiday Report'} exported!`);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message}`);
    }
  };

  const handleExportQuickBooksTime = () => {
    try {
      if (!filters.start_date || !filters.end_date) {
        toast.error('Please select a date range first');
        return;
      }

      const token = sessionStorage.getItem('managerToken');
      
      // Create a form and submit it for download
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = `${API}/reports/quickbooks-time`;
      form.style.display = 'none';
      
      // Add start_date as hidden input
      const startDateInput = document.createElement('input');
      startDateInput.type = 'hidden';
      startDateInput.name = 'start_date';
      startDateInput.value = filters.start_date;
      form.appendChild(startDateInput);
      
      // Add end_date as hidden input
      const endDateInput = document.createElement('input');
      endDateInput.type = 'hidden';
      endDateInput.name = 'end_date';
      endDateInput.value = filters.end_date;
      form.appendChild(endDateInput);
      
      // Add token as hidden input
      if (token) {
        const tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = 'token';
        tokenInput.value = token;
        form.appendChild(tokenInput);
      }
      
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      
      toast.success('QuickBooks Time exported!');
      
    } catch (error) {
      console.error('QuickBooks Time export error:', error);
      toast.error(`Export failed: ${error.message}`);
    }
  };


  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatHoursMinutes = (totalHours) => {
    if (!totalHours) return '-';
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const getTotalHours = () => {
    return timesheets
      .filter((entry) => entry.total_hours)
      .reduce((sum, entry) => sum + entry.total_hours, 0)
      .toFixed(2);
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    
    // Convert UTC times to local time for datetime-local input
    const convertToLocalDatetime = (isoString) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      // Get local timezone offset
      const offset = date.getTimezoneOffset();
      // Adjust for timezone
      const localDate = new Date(date.getTime() - (offset * 60 * 1000));
      // Format as YYYY-MM-DDTHH:MM for datetime-local input
      return localDate.toISOString().slice(0, 16);
    };
    
    setEditForm({
      clock_in: convertToLocalDatetime(entry.clock_in),
      clock_out: convertToLocalDatetime(entry.clock_out),
      reason_for_change: '', // Reset reason field
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEntry = async () => {
    if (!editForm.clock_in) {
      toast.error('Clock in time is required');
      return;
    }

    if (!editForm.reason_for_change || editForm.reason_for_change.trim() === '') {
      toast.error('Reason for change is required');
      return;
    }

    // Check if clock out time is in the future
    if (editForm.clock_out) {
      const clockOutDate = new Date(editForm.clock_out);
      const now = new Date();
      if (clockOutDate > now) {
        toast.error('Clock out time cannot be in the future');
        return;
      }
    }

    try {
      const token = sessionStorage.getItem('managerToken');
      const clockInDate = new Date(editForm.clock_in);
      const clockOutDate = editForm.clock_out ? new Date(editForm.clock_out) : null;
      
      let totalHours = null;
      if (clockOutDate) {
        totalHours = (clockOutDate - clockInDate) / (1000 * 60 * 60);
      }

      await axios.put(
        `${API}/timesheets/${selectedEntry.id}`,
        {
          clock_in: clockInDate.toISOString(),
          clock_out: clockOutDate ? clockOutDate.toISOString() : null,
          total_hours: totalHours ? parseFloat(totalHours.toFixed(2)) : null,
          reason_for_change: editForm.reason_for_change.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Time entry updated successfully');
      setEditDialogOpen(false);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating time entry');
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(timesheets.map(entry => entry.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectEntry = (entryId) => {
    setSelectedEntries(prev => {
      if (prev.includes(entryId)) {
        return prev.filter(id => id !== entryId);
      } else {
        return [...prev, entryId];
      }
    });
  };

  const handleArchiveSelected = async () => {
    if (selectedEntries.length === 0) {
      toast.error('No entries selected');
      return;
    }

    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.patch(
        `${API}/timesheets/archive`,
        { entry_ids: selectedEntries },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(`Successfully archived ${selectedEntries.length} time entries`);
      setSelectedEntries([]);
      setSelectAll(false);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error archiving entries');
    }
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;

    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.delete(`${API}/timesheets/${entryToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Time entry deleted successfully');
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting entry');
    }
  };

  const openDeleteDialog = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  return (
    <ManagerLayout manager={manager} onLogout={onLogout}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Timesheets</h1>
        <p className="text-gray-600">View and export employee time records</p>
      </div>

      <Card className="p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-800">Filters</h2>
        
        {/* Preset Date Ranges */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Date Ranges
          </label>
          <Select
            value={filters.preset || 'custom'}
            onValueChange={handlePresetChange}
          >
            <SelectTrigger data-testid="preset-filter" className="w-full md:w-64">
              <SelectValue placeholder="Select date range preset" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="custom">Custom Date Range</SelectItem>
              <SelectItem value="prior_week">Prior Week - Mon to Sun</SelectItem>
              <SelectItem value="prior_2_weeks">Prior 2 Weeks - Mon to Sun</SelectItem>
              <SelectItem value="first_half">1st - 14th of This Month</SelectItem>
              <SelectItem value="second_half">15th - End of This Month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) =>
                setFilters({ ...filters, start_date: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
              data-testid="start-date-filter"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) =>
                setFilters({ ...filters, end_date: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
              data-testid="end-date-filter"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <Select
              value={filters.location_id || "all"}
              onValueChange={(value) =>
                setFilters({ ...filters, location_id: value === "all" ? "" : value })
              }
            >
              <SelectTrigger data-testid="location-filter">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee
            </label>
            <Select
              value={filters.employee_id || "all"}
              onValueChange={(value) =>
                setFilters({ ...filters, employee_id: value === "all" ? "" : value })
              }
            >
              <SelectTrigger data-testid="employee-filter">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status (Multi-select)
            </label>
            <div className="border border-gray-300 rounded-md p-3 space-y-2">
              {['normal', 'edited', 'archived'].map((status) => (
                <label key={status} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.status.includes(status)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFilters({ 
                          ...filters, 
                          status: [...filters.status, status] 
                        });
                      } else {
                        setFilters({ 
                          ...filters, 
                          status: filters.status.filter(s => s !== status) 
                        });
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm capitalize">{status}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <Button
            onClick={() => {
              if (currentView === 'timesheets') {
                fetchTimesheets();
              } else if (currentView === 'overtime') {
                fetchOvertimeData();
              } else if (currentView === 'stat-holiday') {
                fetchStatHolidayData();
              }
            }}
            disabled={loading}
            data-testid="apply-filters-button"
          >
            {loading ? 'Loading...' : 'Apply Filters'}
          </Button>
        </div>
      </Card>

      {/* View Selector */}
      <Card className="p-4 mb-4">
        <div className="flex gap-2">
          <Button
            onClick={() => setCurrentView('timesheets')}
            variant={currentView === 'timesheets' ? 'default' : 'outline'}
            className={currentView === 'timesheets' ? 'bg-gray-900' : ''}
          >
            Timesheets
          </Button>
          <Button
            onClick={() => {
              setCurrentView('overtime');
              fetchOvertimeData();
            }}
            variant={currentView === 'overtime' ? 'default' : 'outline'}
            className={currentView === 'overtime' ? 'bg-gray-900' : ''}
          >
            Overtime Report
          </Button>
          <Button
            onClick={() => {
              setCurrentView('stat-holiday');
              fetchStatHolidayData();
            }}
            variant={currentView === 'stat-holiday' ? 'default' : 'outline'}
            className={currentView === 'stat-holiday' ? 'bg-gray-900' : ''}
          >
            Stat Holiday Report
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-800">
              {currentView === 'timesheets' && `Time Entries (${timesheets.length})`}
              {currentView === 'overtime' && `Overtime Report (${overtimeData.length} entries)`}
              {currentView === 'stat-holiday' && `Stat Holiday Report (${statHolidayData.length} entries)`}
            </h2>
            {currentView === 'timesheets' && selectedEntries.length > 0 && (
              <Button
                onClick={handleArchiveSelected}
                variant="outline"
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 text-blue-700"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive Selected ({selectedEntries.length})
              </Button>
            )}
          </div>
          <div className="flex items-center gap-4">
            {currentView === 'timesheets' && (
              <div className="text-sm text-gray-600">
                Total Hours: <span className="font-bold text-gray-800">{getTotalHours()}</span>
              </div>
            )}
            <Button
              onClick={handleExportQuickBooksTime}
              variant="outline"
              className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700"
              data-testid="export-qb-time-button"
            >
              <Download className="w-4 h-4 mr-2" />
              QuickBooks Time
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              className="bg-green-50 hover:bg-green-100 border-green-200"
              data-testid="export-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export to CSV
            </Button>
          </div>
        </div>
        
        {/* Timesheets View */}
        {currentView === 'timesheets' && (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="timesheets-table">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-4 h-4 cursor-pointer"
                    title="Select All"
                  />
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Employee
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Location
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Clock In
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Clock Out
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Hours:Minutes
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Reason For Change
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {timesheets.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b hover:bg-gray-50"
                  data-testid={`timesheet-row-${entry.id}`}
                >
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={selectedEntries.includes(entry.id)}
                      onChange={() => handleSelectEntry(entry.id)}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                  <td className="py-3 px-4 text-gray-800">
                    {entry.employee_name}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {entry.location_name}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {formatDateTime(entry.clock_in)}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {formatDateTime(entry.clock_out)}
                  </td>
                  <td className="py-3 px-4 text-gray-800 font-semibold">
                    {entry.total_hours ? formatHoursMinutes(entry.total_hours) : 'Active'}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        entry.status === 'exception'
                          ? 'bg-orange-100 text-orange-800'
                          : entry.status === 'edited' && entry.original_status === 'auto-logout'
                          ? 'bg-purple-100 text-purple-800'
                          : entry.status === 'edited'
                          ? 'bg-yellow-100 text-yellow-800'
                          : entry.status === 'archived'
                          ? 'bg-gray-100 text-gray-800'
                          : entry.status === 'auto-logout'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {entry.status === 'edited' && entry.original_status === 'auto-logout' 
                        ? 'auto-logout ED' 
                        : entry.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-600 text-sm max-w-xs truncate" title={entry.reason_for_change || ''}>
                    {entry.reason_for_change || '-'}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEditEntry(entry)}
                        variant="outline"
                        size="sm"
                        data-testid={`edit-entry-${entry.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {manager?.role === 'Administrator' && (
                      <Button
                        onClick={() => openDeleteDialog(entry)}
                        variant="outline"
                        size="sm"
                        data-testid={`delete-entry-${entry.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

      {/* Edit Time Entry Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-timesheet-dialog">
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedEntry && (
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">
                  <strong>Employee:</strong> {selectedEntry.employee_name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Location:</strong> {selectedEntry.location_name}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clock In
              </label>
              <Input
                type="datetime-local"
                value={editForm.clock_in}
                onChange={(e) =>
                  setEditForm({ ...editForm, clock_in: e.target.value })
                }
                data-testid="edit-clock-in"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clock Out (optional)
              </label>
              <Input
                type="datetime-local"
                value={editForm.clock_out}
                onChange={(e) =>
                  setEditForm({ ...editForm, clock_out: e.target.value })
                }
                data-testid="edit-clock-out"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Change <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black resize-none"
                placeholder="Enter reason for changing this time entry (required)"
                value={editForm.reason_for_change}
                onChange={(e) =>
                  setEditForm({ ...editForm, reason_for_change: e.target.value })
                }
                rows="3"
                data-testid="reason-for-change"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will be logged in the Edit Log for audit purposes
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleUpdateEntry}
                className="flex-1"
                data-testid="save-edit-button"
              >
                Save Changes
              </Button>
              <Button
                onClick={() => setEditDialogOpen(false)}
                variant="outline"
                className="flex-1"
                data-testid="cancel-edit-button"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-timesheet-dialog">
          <DialogHeader>
            <DialogTitle>Delete Time Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-gray-700">
              Are you sure you want to permanently delete this time entry?
            </p>
            {entryToDelete && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600">
                  <strong>Employee:</strong> {entryToDelete.employee_name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Date:</strong> {formatDateTime(entryToDelete.clock_in)}
                </p>
              </div>
            )}
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded">
              ⚠️ This action cannot be undone. This time entry will be permanently removed from the database.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                data-testid="cancel-delete-timesheet"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteEntry}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-timesheet"
              >
                Delete Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

        {/* Overtime Report View */}
        {currentView === 'overtime' && (
          <div className="overflow-x-auto mt-4">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading overtime data...</p>
              </div>
            ) : !filters.start_date || !filters.end_date ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium mb-2">No date range selected</p>
                <p className="text-sm">Please select a date range using the filters above to view the Overtime Report</p>
              </div>
            ) : overtimeData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium mb-2">No overtime in current date range</p>
                <p className="text-sm">All employees worked 44 hours or less per week</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Employee Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Username</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Week Start</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Week End</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Regular Hours</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Overtime Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {overtimeData.map((row, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">{row['Employee Name']}</td>
                      <td className="py-3 px-4 text-sm">{row['username']}</td>
                      <td className="py-3 px-4 text-sm">{row['Week Start Date (Monday)']}</td>
                      <td className="py-3 px-4 text-sm">{row['Week End Date (Sunday)']}</td>
                      <td className="py-3 px-4 text-sm">{row['Regular Hours (up to 44)']}</td>
                      <td className="py-3 px-4 text-sm font-bold text-orange-700">{row['Overtime Hours (over 44)']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Stat Holiday Report View */}
        {currentView === 'stat-holiday' && (
          <div className="overflow-x-auto mt-4">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading stat holiday data...</p>
              </div>
            ) : !filters.start_date || !filters.end_date ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium mb-2">No date range selected</p>
                <p className="text-sm">Please select a date range using the filters above to view the Stat Holiday Report</p>
              </div>
            ) : statHolidayData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium mb-2">No stat holidays in current date range</p>
                <p className="text-sm">No Ontario statutory holidays fall within the selected dates</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Employee Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Holiday Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Holiday Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Hours Worked</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Last Day Worked</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">First Day Worked</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Spread</th>
                  </tr>
                </thead>
                <tbody>
                  {statHolidayData.map((row, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">{row['Employee Name']}</td>
                      <td className="py-3 px-4 text-sm font-medium">{row['Holiday Name']}</td>
                      <td className="py-3 px-4 text-sm">{row['Holiday Date']}</td>
                      <td className="py-3 px-4 text-sm font-bold text-blue-700">{row['Hours Worked']}</td>
                      <td className="py-3 px-4 text-sm text-green-700">{row['Last Day Worked']}</td>
                      <td className="py-3 px-4 text-sm text-purple-700">{row['First Day Worked']}</td>
                      <td className="py-3 px-4 text-sm font-bold text-orange-600">{row['Spread']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>

      {/* Dialogs remain below - removing old report dialogs as they're now inline */}
    </ManagerLayout>
  );
};

export default ManagerTimesheets;
